import { cn } from "@/lib/utils";
import { VariantProps, cva } from "class-variance-authority";
import { ComponentProps } from "react";

const textVariants = cva("", {
  variants: {
    variant: {
      h1: "text-[32px] font-normal leading-[110%] tracking-[-1px] lg:text-[48px]",
      h2: "text-[28px] font-normal leading-[110%] tracking-[-1px] lg:text-[42px]",
      h3: "text-[24px] font-normal leading-[110%] tracking-[-1px] lg:text-[36px]",
      h4: "text-[20px] font-normal leading-[110%] lg:text-[24px]",
      h5: "text-[20px] font-normal leading-[150%] lg:text-[24px]",
      h6: "text-[16px] font-normal leading-[110%] lg:text-[18px]",
      p: "text-[14px] font-normal leading-[110%] lg:text-[16px]",
      lead: "text-muted-foreground text-[16px] font-normal leading-[110%] lg:text-[18px]",
      large: "text-[16px] font-normal leading-[110%] lg:text-[18px]",
      small: "text-[10px] font-normal leading-[110%] lg:text-[12px]",
      muted:
        "text-muted-foreground text-[14px] font-normal leading-[110%] lg:text-[16px]",
      blockquote: "mt-6 border-l-2 pl-6 italic",
      list: "my-6 ml-6 list-disc [&>li]:mt-2",
      inlineCode:
        "bg-muted font-mono relative rounded px-[0.3rem] py-[0.2rem] text-sm",
    },
    weight: {
      thin: "font-thin",
      extralight: "font-extralight",
      light: "font-light",
      normal: "font-normal",
      medium: "font-medium",
      semibold: "font-semibold",
      bold: "font-bold",
      extrabold: "font-extrabold",
      black: "font-black",
    },
    align: {
      left: "text-left",
      center: "text-center",
      right: "text-right",
    },
  },
  defaultVariants: {
    variant: "p",
    weight: "normal",
    align: "left",
  },
});

type TextVariantProps = VariantProps<typeof textVariants>;

interface BaseTextProps extends TextVariantProps {
  className?: string;
}

// Base Text Component
const Text = ({
  className,
  variant,
  weight,
  align,
  as: Component = "p",
  ...props
}: BaseTextProps & ComponentProps<"p"> & { as?: "p" | "span" | "div" }) => {
  return (
    <Component
      className={cn(textVariants({ variant, weight, align }), className)}
      {...props}
    />
  );
};

// Heading Component
const Heading = ({
  className,
  variant,
  weight,
  align,
  as: Component = "h1",
  ...props
}: BaseTextProps &
  ComponentProps<"h1"> & { as?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" }) => {
  return (
    <Component
      className={cn(textVariants({ variant, weight, align }), className)}
      {...props}
    />
  );
};

// Blockquote Component
const BlockquoteText = ({
  className,
  variant,
  weight,
  align,
  ...props
}: BaseTextProps & ComponentProps<"blockquote">) => {
  return (
    <blockquote
      className={cn(textVariants({ variant, weight, align }), className)}
      {...props}
    />
  );
};

// List Component
const ListText = ({
  className,
  variant,
  weight,
  align,
  ...props
}: BaseTextProps & ComponentProps<"ul">) => {
  return (
    <ul
      className={cn(textVariants({ variant, weight, align }), className)}
      {...props}
    />
  );
};

// Code Component
const CodeText = ({
  className,
  variant,
  weight,
  align,
  ...props
}: BaseTextProps & ComponentProps<"code">) => {
  return (
    <code
      className={cn(textVariants({ variant, weight, align }), className)}
      {...props}
    />
  );
};

// Convenience components for common text patterns
const H1 = (props: Omit<BaseTextProps & ComponentProps<"h1">, "variant">) => (
  <Heading as="h1" variant="h1" {...props} />
);
const H2 = (props: Omit<BaseTextProps & ComponentProps<"h2">, "variant">) => (
  <Heading as="h2" variant="h2" {...props} />
);
const H3 = (props: Omit<BaseTextProps & ComponentProps<"h3">, "variant">) => (
  <Heading as="h3" variant="h3" {...props} />
);
const H4 = (props: Omit<BaseTextProps & ComponentProps<"h4">, "variant">) => (
  <Heading as="h4" variant="h4" {...props} />
);
const H5 = (props: Omit<BaseTextProps & ComponentProps<"h5">, "variant">) => (
  <Heading as="h5" variant="h5" {...props} />
);
const H6 = (props: Omit<BaseTextProps & ComponentProps<"h6">, "variant">) => (
  <Heading as="h6" variant="h6" {...props} />
);
const P = (props: Omit<BaseTextProps & ComponentProps<"p">, "variant">) => (
  <Text as="p" variant="p" {...props} />
);
const Lead = (props: Omit<BaseTextProps & ComponentProps<"p">, "variant">) => (
  <Text as="p" variant="lead" {...props} />
);
const Large = (props: Omit<BaseTextProps & ComponentProps<"p">, "variant">) => (
  <Text as="p" variant="large" {...props} />
);
const Small = (props: Omit<BaseTextProps & ComponentProps<"p">, "variant">) => (
  <Text as="p" variant="small" {...props} />
);
const Muted = (props: Omit<BaseTextProps & ComponentProps<"p">, "variant">) => (
  <Text as="p" variant="muted" {...props} />
);
const Blockquote = (
  props: Omit<BaseTextProps & ComponentProps<"blockquote">, "variant">
) => <BlockquoteText variant="blockquote" {...props} />;
const List = (props: Omit<BaseTextProps & ComponentProps<"ul">, "variant">) => (
  <ListText variant="list" {...props} />
);
const InlineCode = (
  props: Omit<BaseTextProps & ComponentProps<"code">, "variant">
) => <CodeText variant="inlineCode" {...props} />;

export {
  Text,
  H1,
  H2,
  H3,
  H4,
  H5,
  H6,
  P,
  Lead,
  Large,
  Small,
  Muted,
  Blockquote,
  List,
  InlineCode,
};
